/**
 * <g-autopilot> — surfaces what every resident agent in the app is doing.
 *
 * Three rendering modes selected via the `mode` attribute:
 *
 *   mode="strip"  (default) — ambient pill that lives in the topbar.
 *                             Shows "N drafts · M agents". Click → opens
 *                             a popover with the latest drafts. Stays out
 *                             of the way when there's nothing happening.
 *
 *   mode="widget" — first-class dashboard widget. Renders a roster of agents
 *                   in scope, plus a recent-activity timeline. Filterable.
 *                   Sized to fit a card grid cell.
 *
 *   mode="full"   — the dedicated /autopilot page: inbox, activity feed,
 *                   agent roster, objectives, playbook editor scaffolding.
 *                   Phase 1 ships scaffolded sections — Phases 2–3 fill them.
 *
 * Data source: GET /api/agent-actions (+ /summary for counts). The component
 * subscribes to `agent:action` Socket.IO events on `window.appSocket` and
 * patches its in-memory list without re-fetching, the same pattern Alpine's
 * `$store.collections` uses elsewhere in the boilerplate. If `window.appSocket`
 * isn't available yet at construction time the component falls back to a
 * polling refresh (every 15s) — covers SSR / dev-iframe edge cases.
 *
 * Attributes:
 *   mode       'strip' | 'widget' | 'full'   (default 'strip')
 *   scope      'all' | 'app' | 'user:<id>'    (default '' — server picks default)
 *   agent-name filter to a single agent name
 *   limit      number of rows to show (widget=10 / full=50 default)
 *   route      path the strip + widget link through to ('/autopilot' default)
 *
 * Properties (programmatic, take precedence over attributes when set):
 *   .actions   — array of AgentAction docs (auto-populated; setting replaces)
 *   .summary   — { counts: { draft, executed, ... }, agentCount, agents }
 */

const ENDPOINT = '/api/agent-actions';
const POLL_FALLBACK_MS = 15_000;
const RELATIVE_TIME_THRESHOLDS = [
  { unit: 'second', max: 60, divisor: 1 },
  { unit: 'minute', max: 3600, divisor: 60 },
  { unit: 'hour', max: 86_400, divisor: 3600 },
  { unit: 'day', max: 604_800, divisor: 86_400 }
];

export class GAutopilot extends HTMLElement {
  constructor() {
    super();
    this._actions = [];
    this._agents = [];
    this._summary = { counts: {}, agentCount: 0, agents: [] };
    // In-flight agent runs keyed by fireId. Populated by `agent:firing`,
    // updated by `agent:step`, removed when the matching `agent:action`
    // arrives (or after a timeout fallback). Drives the "running…" rows
    // shown above the inbox so the user sees instant feedback after Run-now
    // instead of waiting for the agent loop's end-of-loop write.
    this._pending = new Map();
    this._renderScheduled = false;
    this._socketBound = false;
    this._pollHandle = null;
    this._popoverOpen = false;
    // Full-mode 3-pane state.
    this._selectedAgentId = '__all__';
    this._selectedFolder = 'inbox';
    this._openMessageId = null;
    this._shellRendered = false;
    this._panel = null;
    this._railExpanded = new Set();
    this._onEscBound = null;
  }

  static get observedAttributes() {
    return ['mode', 'scope', 'agent-name', 'limit', 'route'];
  }

  disconnectedCallback() {
    if (this._onEscBound) {
      document.removeEventListener('keydown', this._onEscBound);
      this._onEscBound = null;
    }
    if (this._onResizeBound) {
      window.removeEventListener('resize', this._onResizeBound);
      this._onResizeBound = null;
    }
    if (this._fitObserver) {
      this._fitObserver.disconnect();
      this._fitObserver = null;
    }
    if (this._pollHandle) { clearInterval(this._pollHandle); this._pollHandle = null; }
    if (this._socketWatch) { clearInterval(this._socketWatch); this._socketWatch = null; }
  }

  attributeChangedCallback() {
    this._scheduleRender();
    this._refresh();
  }

  connectedCallback() {
    this.classList.add('block');
    // SSR hydration: when topbar-actions.ejs server-renders the pill HTML
    // inline, it wraps the markup in <div data-g-root>. Reuse that node as
    // _root so we don't append a second empty div alongside it. Without
    // this, the visible pop is back: the SSR pill paints, then upgrade
    // creates a fresh _root and renders into it (sibling), and the SSR
    // content is left orphaned + visible.
    if (!this._root) {
      const ssrRoot = this.querySelector('[data-g-root]');
      if (ssrRoot) {
        this._root = ssrRoot;
      } else {
        this._root = document.createElement('div');
        // Tag the freshly-created root with data-g-root so the host-side
        // defensive CSS rule (`g-autopilot > :not([data-g-root]) { display:
        // none }`) — which exists to hide stray render roots from older
        // cached bundles — doesn't accidentally hide our own root in
        // widget/full modes where SSR doesn't pre-emit one.
        this._root.setAttribute('data-g-root', '');
        this.appendChild(this._root);
      }
    }
    // Server-side pre-fill from data-initial-summary keeps the in-memory
    // _summary in sync with what the SSR pill already shows. Background
    // _refresh updates if there's drift between the cached server count
    // and the live API.
    const initial = this.getAttribute('data-initial-summary');
    if (initial) {
      try {
        const parsed = JSON.parse(initial);
        this._summary = {
          counts: parsed.counts || {},
          agentCount: parsed.agentCount || 0,
          agents: parsed.agents || []
        };
        // Skip _render() on first paint when we hydrated from SSR — the
        // markup is already correct, and re-rendering an identical pill
        // would briefly drop event handlers (or no-op visually).
      } catch { /* malformed JSON — fall back to client render */ }
    }
    // Wire up event handlers on the SSR'd pill (Approve/Skip in dropdown).
    this._bindStripHandlers();
    this._bindSocket();
    this._refresh();
    if (!initial) this._scheduleRender();
  }

  // Attach approve/skip click handlers to whatever pill is currently in
  // _root, whether SSR'd or client-rendered. Idempotent — clones nodes to
  // wipe any prior listeners before re-binding.
  _bindStripHandlers() {
    if (!this._root) return;
    this._root.querySelectorAll('[data-approve]').forEach(btn => {
      const fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      fresh.addEventListener('click', (e) => {
        e.stopPropagation();
        this._post(fresh.getAttribute('data-approve'), 'approve');
      });
    });
    this._root.querySelectorAll('[data-skip]').forEach(btn => {
      const fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      fresh.addEventListener('click', (e) => {
        e.stopPropagation();
        this._post(fresh.getAttribute('data-skip'), 'skip');
      });
    });
  }

  disconnectedCallback() {
    if (this._pollHandle) {
      clearInterval(this._pollHandle);
      this._pollHandle = null;
    }
    if (this._socketWatch) {
      clearInterval(this._socketWatch);
      this._socketWatch = null;
    }
  }

  // ── Public API ──

  get actions() { return this._actions; }
  set actions(rows) {
    this._actions = Array.isArray(rows) ? rows : [];
    this._scheduleRender();
  }

  get summary() { return this._summary; }
  set summary(s) {
    this._summary = s && typeof s === 'object' ? s : { counts: {}, agentCount: 0, agents: [] };
    this._scheduleRender();
  }

  // ── Data fetching ──

  async _refresh() {
    if (!this.isConnected) return;
    try {
      const limit = this._mode() === 'full' ? 50 : 10;
      const params = new URLSearchParams();
      params.set('limit', String(this.getAttribute('limit') || limit));
      const scope = this.getAttribute('scope');
      if (scope) params.set('scope', scope);
      const agentName = this.getAttribute('agent-name');
      if (agentName) params.set('agentName', agentName);

      // Full mode also fetches the AutopilotAgent roster so it can render
      // a "Running agents" section. Strip + widget skip that fetch — they
      // only need counts (already in summary).
      const fullMode = this._mode() === 'full';
      const fetches = [
        fetch(`${ENDPOINT}?${params.toString()}`).then(r => r.ok ? r.json() : { data: [] }),
        fetch(`${ENDPOINT}/summary${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`)
          .then(r => r.ok ? r.json() : { counts: {}, agentCount: 0, agents: [] })
      ];
      if (fullMode) {
        fetches.push(
          fetch('/api/autopilot-agents').then(r => r.ok ? r.json() : { data: [] })
        );
      }
      const [actionsRes, summaryRes, agentsRes] = await Promise.all(fetches);
      this._actions = actionsRes.data || [];
      this._summary = {
        counts: summaryRes.counts || {},
        agentCount: summaryRes.agentCount || 0,
        agents: summaryRes.agents || []
      };
      if (fullMode) this._agents = agentsRes?.data || [];
      this._scheduleRender();
    } catch (err) {
      console.warn('[g-autopilot] refresh failed:', err.message);
    }
  }

  _bindSocket() {
    if (this._socketBound) return;
    const sock = typeof window !== 'undefined' ? window.appSocket : null;
    if (sock && typeof sock.on === 'function') {
      this._socketBound = true;
      console.log('[g-autopilot] bindSocket: bound to window.appSocket (connected=' + sock.connected + ')');
      sock.on('agent:action', (payload) => { console.log('[g-autopilot] agent:action', payload); this._onActionEvent(payload); });
      sock.on('agent:firing', (payload) => { console.log('[g-autopilot] agent:firing', payload); this._onFiringEvent(payload); });
      sock.on('agent:step', (payload) => { console.log('[g-autopilot] agent:step', payload); this._onStepEvent(payload); });
      // Stop polling now that real-time events are live.
      if (this._pollHandle) {
        clearInterval(this._pollHandle);
        this._pollHandle = null;
      }
      if (this._socketWatch) {
        clearInterval(this._socketWatch);
        this._socketWatch = null;
      }
      return;
    }
    // socket-client.js loads after g-autopilot in the document; window.appSocket
    // doesn't exist yet at first connectedCallback. Poll for it on a short
    // cadence so we bind the moment it appears, and keep slow polling as a
    // safety net for the gap.
    if (!this._pollHandle) {
      this._pollHandle = setInterval(() => this._refresh(), POLL_FALLBACK_MS);
    }
    if (!this._socketWatch) {
      console.log('[g-autopilot] bindSocket: window.appSocket missing — watching for it (will rebind when ready)');
      let attempts = 0;
      this._socketWatch = setInterval(() => {
        attempts++;
        if (window.appSocket && typeof window.appSocket.on === 'function') {
          clearInterval(this._socketWatch);
          this._socketWatch = null;
          this._bindSocket();
        } else if (attempts > 100) {
          // 100 × 200ms = 20s. Give up — polling stays on.
          clearInterval(this._socketWatch);
          this._socketWatch = null;
          console.warn('[g-autopilot] bindSocket: window.appSocket never appeared, staying on poll-only mode');
        }
      }, 200);
    }
  }

  _onFiringEvent(payload) {
    if (!payload || !payload.fireId) return;
    this._pending.set(payload.fireId, {
      ...payload,
      step: 0,
      maxSteps: null,
      startedAt: payload.firedAt || Date.now()
    });
    // Safety net — clear stale placeholders if no `agent:action` arrives.
    // Resident agent caps at maxSteps with a generous bound; 60s is roomy.
    if (this._pendingTimeouts) clearTimeout(this._pendingTimeouts.get?.(payload.fireId));
    else this._pendingTimeouts = new Map();
    this._pendingTimeouts.set(payload.fireId, setTimeout(() => {
      this._pending.delete(payload.fireId);
      this._pendingTimeouts.delete(payload.fireId);
      this._scheduleRender();
    }, 60_000));
    this._scheduleRender();
  }

  _onStepEvent(payload) {
    if (!payload || !payload.fireId) return;
    const entry = this._pending.get(payload.fireId);
    if (!entry) return;
    entry.step = payload.step;
    entry.maxSteps = payload.maxSteps;
    entry.lastStopReason = payload.stopReason;
    this._scheduleRender();
  }

  _clearPending(fireId) {
    if (!fireId) return;
    if (this._pending.has(fireId)) this._pending.delete(fireId);
    if (this._pendingTimeouts?.has(fireId)) {
      clearTimeout(this._pendingTimeouts.get(fireId));
      this._pendingTimeouts.delete(fireId);
    }
  }

  _onActionEvent(payload) {
    if (!payload || !payload._id) return;
    // Settled — drop any matching placeholder before rendering.
    this._clearPending(payload.fireId);
    const idx = this._actions.findIndex(a => a._id === payload._id);
    const prevStatus = idx >= 0 ? this._actions[idx].status : null;
    if (idx >= 0) {
      this._actions.splice(idx, 1, payload);
    } else {
      this._actions.unshift(payload);
      // Keep the in-memory list bounded so a chatty agent doesn't grow it forever.
      const cap = parseInt(this.getAttribute('limit'), 10) || 50;
      if (this._actions.length > cap) this._actions.length = cap;
    }
    // Optimistic count bump so the topbar pill badge updates with the
    // socket event instead of waiting for the summary fetch round-trip.
    // _refreshSummary then reconciles any drift below.
    if (!this._summary.counts) this._summary.counts = {};
    if (prevStatus && prevStatus !== payload.status) {
      this._summary.counts[prevStatus] = Math.max(0, (this._summary.counts[prevStatus] || 0) - 1);
    }
    if (!prevStatus || prevStatus !== payload.status) {
      this._summary.counts[payload.status] = (this._summary.counts[payload.status] || 0) + 1;
    }
    // Authoritative refetch — usually a no-op; corrects any drift.
    this._refreshSummary();
    this._scheduleRender();
  }

  async _refreshSummary() {
    try {
      const scope = this.getAttribute('scope');
      const r = await fetch(`${ENDPOINT}/summary${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`);
      if (!r.ok) return;
      const j = await r.json();
      this._summary = {
        counts: j.counts || {},
        agentCount: j.agentCount || 0,
        agents: j.agents || []
      };
      // Re-render after the authoritative fetch — without this the strip
      // pill stayed stale even after _summary was updated, because the
      // earlier _onActionEvent's _scheduleRender already ran with the
      // pre-fetch (optimistic) counts.
      this._scheduleRender();
    } catch { /* non-fatal */ }
  }

  // ── Action mutations ──

  async _post(id, suffix) {
    try {
      const r = await fetch(`${ENDPOINT}/${id}/${suffix}`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) {
        console.warn(`[g-autopilot] ${suffix} failed:`, j.error || r.status);
        return;
      }
      // Optimistic update: apply the server's response payload directly
      // instead of waiting for the socket round-trip. The socket emit
      // arrives shortly after with the same data — no-op replace. Without
      // this, a missed socket event (race / disconnected client / handler
      // error) means the inbox stays stale until the 15s fallback poll.
      if (j.data) this._onActionEvent(j.data);
    } catch (err) {
      console.warn(`[g-autopilot] ${suffix} error:`, err.message);
    }
  }

  // ── Rendering ──

  _mode() {
    const m = (this.getAttribute('mode') || 'strip').toLowerCase();
    return ['strip', 'widget', 'full'].includes(m) ? m : 'strip';
  }

  _scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    const mode = this._mode();
    if (mode === 'strip') this._renderStrip();
    else if (mode === 'widget') this._renderWidget();
    else this._renderFull();
  }

  _renderStrip() {
    const draftCount = this._summary.counts.draft || 0;
    const agentCount = this._summary.agentCount || 0;
    const route = this.getAttribute('route') || '/autopilot';

    // Hide entirely when there's nothing to surface.
    if (agentCount === 0 && draftCount === 0) {
      this._root.innerHTML = '';
      return;
    }

    // Icon-only btn-square — same shape as Messenger/Notifications. Red
    // dot in the top-right when drafts are pending; absent otherwise.
    const drafts = this._actions.filter(a => a.status === 'draft').slice(0, 5);
    const pendingFires = Array.from(this._pending.values());
    // Match the Weavy messenger/notification dots visually — primary
    // brand orange, top-right corner. Weavy's <wy-*-badge> components
    // pick up the brand color from their own theme; bg-primary on a
    // sibling button puts our dot on the same swatch.
    const dot = draftCount > 0
      ? `<span class="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-primary pointer-events-none" aria-hidden="true"></span>`
      : '';
    // While an agent is firing, show an animated info dot — same corner as
    // the draft dot but offset slightly so both are visible if drafts also
    // exist. Distinct color so "running" reads differently from "waiting".
    const runningDot = pendingFires.length > 0
      ? `<span class="absolute top-0.5 left-0.5 w-2 h-2 rounded-full bg-info animate-pulse pointer-events-none" aria-hidden="true"></span>`
      : '';
    const tooltip = pendingFires.length > 0
      ? `Autopilot — ${pendingFires.length} agent${pendingFires.length === 1 ? '' : 's'} running`
      : draftCount > 0
        ? `Autopilot — ${draftCount} draft${draftCount === 1 ? '' : 's'} waiting`
        : `Autopilot — ${agentCount} agent${agentCount === 1 ? '' : 's'} active`;

    this._root.innerHTML = `
    <div class="dropdown dropdown-end">
      <button tabindex="0" class="btn btn-ghost btn-square btn-sm relative" aria-label="${escapeAttr(tooltip)}" title="${escapeAttr(tooltip)}">
        <i class="fa-regular fa-microchip-ai text-base"></i>
        ${dot}
        ${runningDot}
      </button>
      <div tabindex="0" class="dropdown-content menu bg-base-100 rounded-box shadow-lg mt-2 w-80 p-3 z-50 border border-base-300">
        <div class="flex items-center justify-between mb-2 px-1">
          <p class="font-semibold text-sm">Autopilot</p>
          <a href="${escapeAttr(route)}" class="text-xs link link-primary">Open</a>
        </div>
        ${pendingFires.length > 0
          ? `<div class="mb-2 space-y-1">${pendingFires.map(p => pendingRow(p)).join('')}</div>`
          : ''}
        ${drafts.length === 0
          ? `<p class="text-xs text-base-content/60 px-1 py-3">${pendingFires.length > 0 ? 'No drafts waiting.' : 'No drafts waiting.'}</p>`
          : drafts.map(a => stripDraftRow(a)).join('')}
      </div>
    </div>
  `;

    // Inline approve / skip handlers — delegated since dropdown menu items
    // aren't reliably reachable as direct refs after dropdown re-render.
    this._root.querySelectorAll('[data-approve]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._post(btn.getAttribute('data-approve'), 'approve');
      });
    });
    this._root.querySelectorAll('[data-skip]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._post(btn.getAttribute('data-skip'), 'skip');
      });
    });
  }

  _renderWidget() {
    const agentCount = this._summary.agentCount || 0;
    const draftCount = this._summary.counts.draft || 0;
    const executedCount = this._summary.counts.executed || 0;
    const recent = this._actions.slice(0, 6);
    const route = this.getAttribute('route') || '/autopilot';

    this._root.innerHTML = `
    <div class="card bg-base-100 shadow-sm h-full">
      <div class="card-body p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <i class="fa-regular fa-robot"></i>
            </div>
            <p class="font-semibold text-sm">Autopilot</p>
          </div>
          <a href="${escapeAttr(route)}" class="link link-primary text-xs">View all</a>
        </div>
        <div class="grid grid-cols-3 gap-2 mt-3 text-center">
          <div>
            <div class="text-xl font-bold">${agentCount}</div>
            <div class="text-[10px] uppercase tracking-wider text-base-content/60">Agents</div>
          </div>
          <div>
            <div class="text-xl font-bold text-warning">${draftCount}</div>
            <div class="text-[10px] uppercase tracking-wider text-base-content/60">Drafts</div>
          </div>
          <div>
            <div class="text-xl font-bold text-success">${executedCount}</div>
            <div class="text-[10px] uppercase tracking-wider text-base-content/60">Executed</div>
          </div>
        </div>
        <div class="divider my-2 text-[10px] opacity-60">RECENT ACTIVITY</div>
        ${recent.length === 0
          ? `<p class="text-xs text-base-content/60 py-3 text-center">No activity yet.</p>`
          : `<ul class="space-y-2">${recent.map(a => widgetRow(a)).join('')}</ul>`}
      </div>
    </div>
  `;
  }

  _renderFull() {
    // First call mounts the 3-column shell + binds delegation. Subsequent
    // calls re-run targeted sub-renders only — keeps panel state, scroll
    // position, and reasoning <details> open-state intact across socket
    // events.
    if (!this._shellRendered) {
      this._renderFullShell();
      return;
    }
    this._renderRail();
    this._renderInboxList();
    if (this._openMessageId) this._renderPanel();
  }

  _renderFullShell() {
    this._root.innerHTML = `
    <div class="flex border border-base-300 rounded-box bg-base-100 overflow-hidden relative" data-autopilot-shell>
      <aside class="w-64 shrink-0 border-r border-base-300 overflow-y-auto bg-base-200/40 text-sm" data-col-rail></aside>
      <section class="flex-1 min-w-0 flex flex-col" data-col-inbox>
        <header class="border-b border-base-300 bg-base-200/40 shrink-0" data-col-inbox-header></header>
        <ul class="flex-1 overflow-y-auto divide-y divide-[rgba(0,0,0,0.06)] m-0 p-0 list-none" data-col-inbox-list></ul>
      </section>
    </div>`;
    this._shellRendered = true;
    this._panel = null;
    this._setupShellDelegation();
    // Defer first fit until after layout has settled (fonts, chrome init).
    requestAnimationFrame(() => this._fitShellHeight());
    // Re-fit when the scroll container resizes (sidebar collapse, devtools).
    if (typeof ResizeObserver !== 'undefined') {
      this._fitObserver = new ResizeObserver(() => this._fitShellHeight());
      // Observe the shell's parent — it grows/shrinks with chrome.
      const parent = this._root.parentElement || this._root;
      this._fitObserver.observe(parent);
    }
    this._renderRail();
    this._renderInboxHeader();
    this._renderInboxList();
  }

  // Fits the shell to the available space inside its scrolling ancestor.
  // The bottom gutter is split across multiple ancestors (the chrome's
  // scroll container has no padding, but #layout-content inside it has
  // 1.5rem padding-bottom). We walk from the shell up to the scroller
  // summing every ancestor's padding-bottom + border-bottom-width so the
  // gutter is fully respected. Magic-number viewport math (h-[calc(...)])
  // can't see this and drifts on every chrome variant.
  _fitShellHeight() {
    const shell = this._root?.querySelector('[data-autopilot-shell]');
    if (!shell || !shell.isConnected) return;

    // Walk up: sum padding-bottom + border-bottom-width of every ancestor
    // up to (and including the inner edge of) the scroll container.
    let guttersBelow = 0;
    let scroller = null;
    let el = shell.parentElement;
    while (el && el !== document.body) {
      const cs = getComputedStyle(el);
      guttersBelow += parseFloat(cs.paddingBottom) || 0;
      guttersBelow += parseFloat(cs.borderBottomWidth) || 0;
      if (/(auto|scroll|overlay)/.test(cs.overflowY)) { scroller = el; break; }
      el = el.parentElement;
    }
    const container = scroller || document.documentElement;
    const containerRect = container.getBoundingClientRect();
    // Clamp to viewport — if the scroller is somehow taller than viewport
    // (rare, e.g. inside an iframe with overflow), viewport is the real cap.
    const innerBottom = Math.min(window.innerHeight, containerRect.bottom);
    const shellRect = shell.getBoundingClientRect();
    const available = Math.max(360, Math.floor(innerBottom - shellRect.top - guttersBelow));
    shell.style.height = `${available}px`;
  }

  _setupShellDelegation() {
    const shell = this._root.querySelector('[data-autopilot-shell]');
    if (!shell) return;

    shell.addEventListener('click', (e) => {
      const folderLink = e.target.closest('[data-agent-id][data-folder]');
      if (folderLink) {
        e.preventDefault();
        this._selectFolder(folderLink.getAttribute('data-agent-id'), folderLink.getAttribute('data-folder'));
        return;
      }
      const groupToggle = e.target.closest('[data-toggle-group]');
      if (groupToggle) {
        this._toggleGroup(groupToggle.getAttribute('data-toggle-group'));
        return;
      }
      const closePanel = e.target.closest('[data-close-panel]');
      if (closePanel) { this._closePanel(); return; }
      const messageRow = e.target.closest('[data-message-id]');
      if (messageRow) {
        this._openMessage(messageRow.getAttribute('data-message-id'));
        return;
      }
      const approveBtn = e.target.closest('[data-approve]');
      if (approveBtn) {
        this._post(approveBtn.getAttribute('data-approve'), 'approve');
        if (approveBtn.closest('[data-message-panel]')) this._closePanel();
        return;
      }
      const skipBtn = e.target.closest('[data-skip]');
      if (skipBtn) {
        this._post(skipBtn.getAttribute('data-skip'), 'skip');
        if (skipBtn.closest('[data-message-panel]')) this._closePanel();
        return;
      }
      const runBtn = e.target.closest('[data-run-agent]');
      if (runBtn) { this._runAgentNow(runBtn); return; }
      const simBtn = e.target.closest('[data-simulate-agent]');
      if (simBtn) { this._simulateAgent(simBtn); return; }
      // Click on the inbox list background (not a row) closes the panel.
      const list = e.target.closest('[data-col-inbox-list]');
      if (list && !messageRow && this._openMessageId) this._closePanel();
    });

    this._onEscBound = (e) => {
      if (e.key === 'Escape' && this._openMessageId) this._closePanel();
    };
    document.addEventListener('keydown', this._onEscBound);

    this._onResizeBound = () => this._fitShellHeight();
    window.addEventListener('resize', this._onResizeBound);
  }

  _renderRail() {
    const rail = this._root.querySelector('[data-col-rail]');
    if (!rail) return;
    const agents = Array.isArray(this._agents) ? this._agents : [];
    const draftsByAgent = new Map();
    this._actions.forEach(a => {
      if (a.status === 'draft') draftsByAgent.set(a.agentName, (draftsByAgent.get(a.agentName) || 0) + 1);
    });
    const totalDrafts = this._summary.counts.draft || 0;

    const topLinks = [
      { folder: 'inbox', icon: 'fa-regular fa-clipboard-check', label: 'Approvals', count: totalDrafts },
      { folder: 'sent',  icon: 'fa-regular fa-clock-rotate-left', label: 'Activity', count: 0 }
    ].map(it => railTopLink(it, this._selectedAgentId === '__all__' && this._selectedFolder === it.folder)).join('');

    const agentRows = agents.map(agent => {
      const expanded = this._railExpanded.has(agent._id);
      const isCurrent = this._selectedAgentId === agent._id;
      const safeIcon = sanitizeIconClass(agent.icon) || 'fa-regular fa-microchip-ai';
      const draftCount = draftsByAgent.get(agent.name) || 0;
      const subRows = expanded ? `
        <div class="ml-7 mt-0.5 space-y-0.5">
          <a href="#" data-agent-id="${escapeAttr(agent._id)}" data-folder="inbox"
             class="flex items-center gap-2 px-2 py-1 rounded text-sm ${isCurrent && this._selectedFolder === 'inbox' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-base-200'}">
            <i class="fa-regular fa-clipboard-check w-4 text-center text-xs"></i>
            <span class="flex-1">Approvals</span>
            ${draftCount > 0 ? `<span class="badge badge-ghost badge-xs">${draftCount}</span>` : ''}
          </a>
          <a href="#" data-agent-id="${escapeAttr(agent._id)}" data-folder="sent"
             class="flex items-center gap-2 px-2 py-1 rounded text-sm ${isCurrent && this._selectedFolder === 'sent' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-base-200'}">
            <i class="fa-regular fa-clock-rotate-left w-4 text-center text-xs"></i>
            <span class="flex-1">Activity</span>
          </a>
        </div>` : '';
      return `
        <div class="mb-1" data-agent-row="${escapeAttr(agent._id)}">
          <div class="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-base-200" data-toggle-group="${escapeAttr(agent._id)}">
            <i class="fa-light fa-chevron-right transition-transform duration-150 text-base-content/40 text-xs ${expanded ? 'rotate-90' : ''}"></i>
            <div class="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 bg-primary/10 text-primary">
              <i class="${safeIcon} text-xs"></i>
            </div>
            <span class="text-sm flex-1 truncate font-medium">${escapeHtml(agent.name || 'Unnamed agent')}</span>
            ${draftCount > 0 ? `<span class="badge badge-warning badge-xs">${draftCount}</span>` : ''}
          </div>
          ${subRows}
        </div>`;
    }).join('');

    const agentsCount = agents.length;
    rail.innerHTML = `
      <div class="px-4 py-4 bg-base-100 border-b border-base-300 flex items-center gap-2.5">
        <div class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <i class="fa-regular fa-microchip-ai"></i>
        </div>
        <h2 class="text-base font-semibold">Autopilot</h2>
      </div>
      <div class="px-2 py-2 space-y-0.5 border-b border-base-300/40">
        ${topLinks}
      </div>
      <div class="px-4 py-2 flex items-center justify-between">
        <span class="text-xs font-medium text-base-content/60 uppercase tracking-wider">Agents</span>
        <span class="text-[11px] text-base-content/40">${agentsCount} item${agentsCount === 1 ? '' : 's'}</span>
      </div>
      <div class="px-2 pb-2">${agentRows}</div>
    `;
  }

  _renderInboxHeader() {
    const header = this._root.querySelector('[data-col-inbox-header]');
    if (!header) return;
    const agent = this._currentAgent();
    const folderLabel = this._selectedFolder === 'inbox' ? 'Approvals' : 'Activity';
    const folderIcon = this._selectedFolder === 'inbox' ? 'fa-regular fa-clipboard-check' : 'fa-regular fa-clock-rotate-left';
    const eyebrow = `
      <div class="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-base-content/50 mb-2">
        <i class="${folderIcon}"></i>
        <span>${escapeHtml(folderLabel)}</span>
      </div>`;
    if (!agent) {
      header.innerHTML = `
        <div class="px-6 py-5">
          ${eyebrow}
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-xl bg-base-100 border border-base-300 text-base-content/60 flex items-center justify-center shrink-0">
              <i class="${folderIcon} text-xl"></i>
            </div>
            <div class="min-w-0 flex-1">
              <h1 class="text-xl font-semibold leading-tight">All agents</h1>
              <p class="text-sm text-base-content/60 mt-0.5">Mixed view across every agent in this app.</p>
            </div>
          </div>
        </div>
      `;
      return;
    }
    const triggers = Array.isArray(agent.triggers) ? agent.triggers : [];
    const scheduleParts = triggers.filter(t => t && t.kind === 'schedule').map(t => cronLabel(t.cron));
    const otherKinds = triggers.filter(t => t && t.kind !== 'schedule').map(t => t.kind);
    const triggerLine = [...scheduleParts, ...otherKinds].join(' · ') || 'No trigger';
    const lastFired = agent.lastFiredAt ? `Last fired ${relativeTime(agent.lastFiredAt)} ago` : 'Never fired';
    const safeIcon = sanitizeIconClass(agent.icon) || 'fa-regular fa-microchip-ai';
    const toolbar = agent.paused ? `<span class="badge badge-ghost">paused</span>` : `
      <button type="button"
        class="btn btn-ghost btn-sm gap-1.5"
        data-simulate-agent="${escapeAttr(agent._id)}"
        title="Simulate — preview what the agent would do (no side effects)">
        <i class="fa-regular fa-flask text-primary"></i>Lab
      </button>
      <button type="button"
        class="btn btn-primary btn-sm gap-1.5"
        data-run-agent="${escapeAttr(agent._id)}"
        title="Run now (doesn't affect schedule)">
        <i class="fa-regular fa-play"></i>Run
      </button>`;
    header.innerHTML = `
      <div class="px-6 py-5">
        ${eyebrow}
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <i class="${safeIcon} text-xl"></i>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-3">
              <h1 class="text-xl font-semibold leading-tight truncate flex-1">${escapeHtml(agent.name || 'Unnamed agent')}</h1>
              <div class="flex items-center gap-1.5 shrink-0">${toolbar}</div>
            </div>
            <p class="text-sm text-base-content/70 mt-1 line-clamp-2">${escapeHtml(agent.description || 'No description.')}</p>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2.5 text-xs text-base-content/50">
              <span class="inline-flex items-center gap-1.5">
                <i class="fa-regular fa-clock"></i>${escapeHtml(triggerLine)}
              </span>
              <span class="inline-flex items-center gap-1.5">
                <i class="fa-regular fa-bolt"></i>${escapeHtml(lastFired)}
              </span>
              ${agent.fireCount ? `<span class="inline-flex items-center gap-1.5"><i class="fa-regular fa-rotate"></i>${agent.fireCount} run${agent.fireCount === 1 ? '' : 's'}</span>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _renderInboxList() {
    const list = this._root.querySelector('[data-col-inbox-list]');
    if (!list) return;

    // Snapshot reasoning <details> open state inside the list before swap.
    const openReasonings = new Set();
    list.querySelectorAll('details[data-reasoning-for][open]').forEach(el => {
      openReasonings.add(el.getAttribute('data-reasoning-for'));
    });

    const filtered = this._filteredActions();
    const showPending = this._selectedFolder === 'inbox';
    const pendingFires = showPending ? this._currentPending() : [];

    if (filtered.length === 0 && pendingFires.length === 0) {
      const emptyMsg = this._selectedFolder === 'inbox'
        ? 'Nothing waiting for approval.'
        : 'No activity yet.';
      list.innerHTML = `<li class="p-8 text-center text-sm text-base-content/60">${escapeHtml(emptyMsg)}</li>`;
      return;
    }

    const pendingHtml = pendingFires.map(p => `<li class="px-4 py-3 bg-info/5 border-l-2 border-info">${fullPendingRow(p)}</li>`).join('');
    const rowsHtml = filtered.map(a => fullMessageRow(a, this._openMessageId === a._id)).join('');
    list.innerHTML = pendingHtml + rowsHtml;

    // Restore open <details>.
    openReasonings.forEach(id => {
      const el = list.querySelector(`details[data-reasoning-for="${cssAttrEscape(id)}"]`);
      if (el) el.setAttribute('open', '');
    });
  }

  _renderPanel() {
    const shell = this._root.querySelector('[data-autopilot-shell]');
    if (!shell) return;

    if (!this._openMessageId) {
      if (this._panel) {
        this._panel.classList.add('translate-x-full');
      }
      return;
    }

    const action = this._actions.find(a => a._id === this._openMessageId);
    if (!action) {
      // Message no longer exists (e.g. cap eviction) — close.
      this._closePanel();
      return;
    }

    // Snapshot reasoning state inside panel before swap.
    const openReasonings = new Set();
    if (this._panel) {
      this._panel.querySelectorAll('details[data-reasoning-for][open]').forEach(el => {
        openReasonings.add(el.getAttribute('data-reasoning-for'));
      });
    }

    const html = panelContent(action);
    if (!this._panel) {
      this._panel = document.createElement('aside');
      this._panel.className = 'absolute right-0 top-0 bottom-0 w-[440px] z-30 bg-base-100 border-l border-base-300 shadow-2xl flex flex-col translate-x-full transition-transform duration-200 ease-out';
      this._panel.setAttribute('data-message-panel', '');
      this._panel.innerHTML = html;
      shell.appendChild(this._panel);
      // Force reflow before removing the translate so the slide-in animates.
      // eslint-disable-next-line no-unused-expressions
      this._panel.offsetHeight;
      requestAnimationFrame(() => {
        if (this._panel) this._panel.classList.remove('translate-x-full');
      });
    } else {
      this._panel.innerHTML = html;
      this._panel.classList.remove('translate-x-full');
      openReasonings.forEach(id => {
        const el = this._panel.querySelector(`details[data-reasoning-for="${cssAttrEscape(id)}"]`);
        if (el) el.setAttribute('open', '');
      });
    }
  }

  // ── Selection / panel state mutations ──

  _selectFolder(agentId, folder) {
    const changed = this._selectedAgentId !== agentId || this._selectedFolder !== folder;
    this._selectedAgentId = agentId;
    this._selectedFolder = folder;
    // If the open message no longer matches the new filter, close the panel.
    if (this._openMessageId) {
      const stillVisible = this._filteredActions().some(a => a._id === this._openMessageId);
      if (!stillVisible) this._closePanel({ silent: true });
    }
    this._renderRail();
    this._renderInboxHeader();
    this._renderInboxList();
    if (changed && this._openMessageId) this._renderPanel();
  }

  _toggleGroup(key) {
    if (this._railExpanded.has(key)) this._railExpanded.delete(key);
    else this._railExpanded.add(key);
    this._renderRail();
  }

  _openMessage(id) {
    this._openMessageId = id;
    this._renderInboxList();
    this._renderPanel();
  }

  _closePanel(opts = {}) {
    this._openMessageId = null;
    if (this._panel) this._panel.classList.add('translate-x-full');
    if (!opts.silent) this._renderInboxList();
  }

  // ── Filter helpers ──

  _currentAgent() {
    if (this._selectedAgentId === '__all__') return null;
    const agents = Array.isArray(this._agents) ? this._agents : [];
    return agents.find(a => a._id === this._selectedAgentId) || null;
  }

  _filteredActions() {
    const agent = this._currentAgent();
    const byAgent = agent
      ? this._actions.filter(a => a.agentName === agent.name)
      : this._actions.slice();
    return this._selectedFolder === 'inbox'
      ? byAgent.filter(a => a.status === 'draft')
      : byAgent.filter(a => a.status !== 'draft');
  }

  _currentPending() {
    const agent = this._currentAgent();
    const all = Array.from(this._pending.values());
    return agent ? all.filter(p => p.agentName === agent.name) : all;
  }

  // Manual fire — POST /api/autopilot-agents/<id>/run-now and show
  // immediate feedback on the button (icon swap to spinner). The
  // resident agent dispatches in the background; the resulting action
  // arrives via the agent:action socket event a few seconds later, so
  // _refresh isn't needed here.
  async _runAgentNow(btn) {
    const id = btn.getAttribute('data-run-agent');
    if (!id) return;
    const icon = btn.querySelector('i');
    const prevIconClass = icon ? icon.className : null;
    btn.disabled = true;
    if (icon) icon.className = 'fa-regular fa-spinner fa-spin text-primary';
    try {
      const r = await fetch(`/api/autopilot-agents/${id}/run-now`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) {
        console.warn('[g-autopilot] run-now failed:', j.error || r.status);
        if (icon && prevIconClass) icon.className = prevIconClass;
      } else {
        if (icon) icon.className = 'fa-regular fa-check text-success';
        setTimeout(() => {
          if (icon && prevIconClass) icon.className = prevIconClass;
          btn.disabled = false;
        }, 1500);
        return;
      }
    } catch (err) {
      console.warn('[g-autopilot] run-now error:', err.message);
      if (icon && prevIconClass) icon.className = prevIconClass;
    }
    btn.disabled = false;
  }

  // Simulate — POST /api/autopilot-agents/<id>/simulate. The endpoint
  // runs the agent loop end-to-end against live data but persists
  // nothing and dispatches nothing. Result returns inline so we can
  // show a preview modal (no socket round-trip needed). Useful for
  // checking "is this agent's prompt + cron actually going to do
  // something useful?" without waiting for the next cron tick.
  async _simulateAgent(btn) {
    const id = btn.getAttribute('data-simulate-agent');
    if (!id) return;
    const icon = btn.querySelector('i');
    const prevIconClass = icon ? icon.className : null;
    btn.disabled = true;
    if (icon) icon.className = 'fa-regular fa-spinner fa-spin text-primary';
    try {
      const r = await fetch(`/api/autopilot-agents/${id}/simulate`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) {
        console.warn('[g-autopilot] simulate failed:', j.error || r.status);
        this._showSimulateModal({ ok: false, error: j.error || `HTTP ${r.status}` });
      } else {
        this._showSimulateModal({ ok: true, ...j.data });
      }
    } catch (err) {
      console.warn('[g-autopilot] simulate error:', err.message);
      this._showSimulateModal({ ok: false, error: err.message });
    }
    if (icon && prevIconClass) icon.className = prevIconClass;
    btn.disabled = false;
  }

  // Render the simulation result in a daisyUI modal. Same look as the
  // inbox detail panel — summary, reasoning, tool calls — plus a
  // "would auto-approve" badge when the agent's auto-approve matrix
  // would have fired. No persistence; closing the modal forgets it.
  _showSimulateModal(payload) {
    const existing = document.getElementById('g-autopilot-sim-modal');
    if (existing) existing.remove();
    const modal = document.createElement('dialog');
    modal.id = 'g-autopilot-sim-modal';
    modal.className = 'modal';
    modal.innerHTML = simulateModalContent(payload);
    document.body.appendChild(modal);
    modal.addEventListener('close', () => modal.remove());
    modal.showModal();
    modal.querySelectorAll('[data-close-sim]').forEach(b => {
      b.addEventListener('click', () => modal.close());
    });
  }
}

function simulateModalContent(payload) {
  if (!payload.ok) {
    return `
    <div class="modal-box max-w-2xl">
      <h3 class="font-bold text-lg">Simulation failed</h3>
      <p class="py-2 text-sm text-base-content/70">${escapeHtml(payload.error || 'Unknown error')}</p>
      <div class="modal-action">
        <button class="btn btn-sm" data-close-sim>Close</button>
      </div>
    </div>`;
  }
  const r = payload.result || {};
  const draft = r.draft || {};
  const toolCalls = Array.isArray(r.toolCalls) ? r.toolCalls : [];
  const callRows = toolCalls.map(c => `
  <li class="text-xs flex items-center gap-2">
    <span class="${c.status === 'success' ? 'text-success' : 'text-error'}">${c.status === 'success' ? '✓' : '✗'}</span>
    <code class="font-mono">${escapeHtml(c.name || 'unknown')}</code>
    <span class="text-base-content/60 truncate">${escapeHtml(JSON.stringify(c.input || {}).slice(0, 80))}</span>
  </li>`).join('');
  const reasoning = (r.reasoning || '').trim();
  const autoBadge = payload.wouldAutoApprove
    ? `<span class="badge badge-warning badge-sm">would auto-approve</span>`
    : `<span class="badge badge-ghost badge-sm">would await approval</span>`;
  return `
  <div class="modal-box max-w-2xl">
    <h3 class="font-bold text-lg flex items-center gap-2">
      <i class="fa-regular fa-flask text-primary"></i>
      Simulation — ${escapeHtml(payload.agentName || 'agent')}
    </h3>
    <p class="text-xs text-base-content/60 mt-1">
      Preview only. Nothing was persisted or dispatched.
    </p>
    ${draft.summary ? `
      <div class="mt-4">
        <div class="flex items-center gap-2 mb-1">
          <span class="text-xs uppercase font-semibold text-base-content/60">Would draft</span>
          ${autoBadge}
          <span class="badge badge-ghost badge-sm">risk: ${escapeHtml(r.riskTier || 'medium')}</span>
        </div>
        <p class="text-sm">${escapeHtml(draft.summary)}</p>
        ${draft.tool ? `<p class="text-xs text-base-content/60 mt-1">Dispatcher: <code>${escapeHtml(draft.tool)}</code></p>` : ''}
      </div>` : `
      <p class="mt-4 text-sm text-base-content/70">No draft was produced. Status: ${escapeHtml(r.status || 'unknown')}.</p>`}
    ${callRows ? `
      <div class="mt-4">
        <div class="text-xs uppercase font-semibold text-base-content/60 mb-1">Tool calls</div>
        <ul class="space-y-1">${callRows}</ul>
      </div>` : ''}
    ${reasoning ? `
      <div class="mt-4">
        <div class="text-xs uppercase font-semibold text-base-content/60 mb-1">Reasoning</div>
        <pre class="text-xs bg-base-200 rounded p-2 whitespace-pre-wrap max-h-48 overflow-auto">${escapeHtml(reasoning)}</pre>
      </div>` : ''}
    <div class="modal-action">
      <button class="btn btn-sm" data-close-sim>Close</button>
    </div>
  </div>`;
}

// ── Row templates (top-level so they're reusable across modes) ──

// Live placeholder shown while an agent fire is in flight. Driven by
// `agent:firing` (initial mount) + `agent:step` (progress). Cleared when the
// matching `agent:action` arrives or after the safety-net timeout.
function pendingRow(p) {
  const stepLabel = p.maxSteps
    ? `step ${p.step}/${p.maxSteps}`
    : 'starting…';
  return `
  <div class="px-2 py-2 bg-info/10 rounded">
    <div class="flex items-start gap-2">
      <i class="fa-regular fa-spinner fa-spin text-info mt-1 text-xs"></i>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium truncate">${escapeHtml(p.agentName || 'Agent')}</p>
        <p class="text-[11px] text-base-content/60">Running — ${escapeHtml(stepLabel)}</p>
      </div>
    </div>
  </div>
`;
}

function fullPendingRow(p) {
  const stepLabel = p.maxSteps
    ? `step ${p.step}/${p.maxSteps}`
    : 'starting…';
  return `
  <div class="card bg-info/10 shadow-sm border border-info/30">
    <div class="card-body p-4">
      <div class="flex items-center gap-3">
        <i class="fa-regular fa-spinner fa-spin text-info"></i>
        <div class="min-w-0 flex-1">
          <p class="font-medium text-sm">${escapeHtml(p.agentName || 'Agent')}</p>
          <p class="text-xs text-base-content/60">Running — ${escapeHtml(stepLabel)}</p>
        </div>
      </div>
    </div>
  </div>
`;
}

function stripDraftRow(a) {
  return `
  <div class="px-2 py-2 hover:bg-base-200 rounded">
    <div class="flex items-start gap-2">
      <i class="fa-regular fa-circle-dot text-warning mt-1 text-xs"></i>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium truncate">${escapeHtml(a.agentName || 'Agent')}</p>
        <p class="text-[11px] text-base-content/60 line-clamp-2">${escapeHtml(summaryText(a))}</p>
      </div>
    </div>
    <div class="flex items-center justify-end gap-1 mt-1">
      <button class="btn btn-ghost btn-xs" data-skip="${escapeAttr(a._id)}">Skip</button>
      <button class="btn btn-primary btn-xs" data-approve="${escapeAttr(a._id)}">Approve</button>
    </div>
  </div>
`;
}

function widgetRow(a) {
  const dot = statusDot(a.status);
  return `
  <li class="flex items-start gap-2 text-xs">
    <span class="${dot} mt-1 shrink-0"></span>
    <div class="min-w-0 flex-1">
      <p class="font-medium truncate">${escapeHtml(a.agentName || 'Agent')}</p>
      <p class="text-base-content/60 line-clamp-1">${escapeHtml(summaryText(a))}</p>
    </div>
    <span class="text-[10px] text-base-content/40 shrink-0">${escapeHtml(relativeTime(a.createdAt))}</span>
  </li>
`;
}

// Allow only "fa-regular fa-<name>" or "fa-brands fa-<name>" — keeps the
// agent.icon value safe for direct interpolation into class="...".
function sanitizeIconClass(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.trim().match(/^(fa-regular|fa-brands)\s+(fa-[a-z0-9-]+)$/);
  return m ? `${m[1]} ${m[2]}` : null;
}

// Stable cron → human label using the same preset table the trigger
// editor uses elsewhere. Falls back to the raw cron when there's no match.
function cronLabel(cron) {
  const known = {
    '*/5 * * * *':  'Every 5 minutes',
    '*/15 * * * *': 'Every 15 minutes',
    '*/30 * * * *': 'Every 30 minutes',
    '0 * * * *':    'Hourly',
    '0 9 * * *':    'Daily at 9 AM',
    '0 9 * * 1-5':  'Weekdays at 9 AM',
    '0 9 * * 1':    'Mondays at 9 AM',
    '0 9 1 * *':    'Monthly on the 1st',
  };
  return known[cron] || cron || 'No schedule';
}

// Top-level rail link: mixed-across-agents Approvals/Activity. Bound to
// _selectedAgentId === '__all__' + the matching folder.
function railTopLink(it, isSelected) {
  const stateClass = isSelected ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-base-200';
  const badge = it.count > 0
    ? `<span class="badge ${isSelected ? 'badge-primary' : 'badge-ghost'} badge-sm ml-auto">${it.count}</span>`
    : '';
  return `
    <a href="#"
       data-agent-id="__all__"
       data-folder="${escapeAttr(it.folder)}"
       class="flex items-center gap-2 px-2 py-1.5 rounded ${stateClass}">
      <i class="${it.icon} w-4 text-center"></i>
      <span class="text-sm flex-1 truncate">${escapeHtml(it.label)}</span>
      ${badge}
    </a>`;
}

// Single-line Gmail-style message row in col 2. Status dot stays a colored
// dot (per user feedback — never a badge). Bold sender + subject when the
// action is still a draft (the "unread" signal); muted when it's settled.
// Selected row (panel open on it) mirrors the rail's selected-link style:
// bg-primary/10 + text-primary.
function fullMessageRow(a, isSelected) {
  const subject = summaryText(a);
  const when = relativeTime(a.createdAt);
  const isDraft = a.status === 'draft';
  const errorIcon = a.error
    ? `<i class="fa-light fa-triangle-exclamation text-error ms-1" title="${escapeAttr(a.error)}"></i>`
    : '';
  let rowClass, senderClass, subjectClass, timeClass;
  if (isSelected) {
    rowClass = 'bg-primary/10 text-primary';
    senderClass = isDraft ? 'font-semibold' : 'font-medium';
    subjectClass = isDraft ? 'font-medium' : '';
    timeClass = '';
  } else {
    rowClass = 'hover:bg-base-200';
    senderClass = isDraft ? 'font-semibold text-base-content' : 'text-base-content/80';
    subjectClass = isDraft ? 'font-medium text-base-content' : 'text-base-content/80';
    timeClass = isDraft ? 'font-medium text-base-content/70' : 'text-base-content/40';
  }
  return `
    <li data-message-id="${escapeAttr(a._id)}"
        class="cursor-pointer ${rowClass} px-4 py-2.5 flex items-center gap-3 m-0 text-sm">
      <span class="${statusDot(a.status)} shrink-0" title="${escapeAttr(a.status)}"></span>
      <span class="${senderClass} shrink-0 w-44 truncate">${escapeHtml(a.agentName || 'Agent')}</span>
      <span class="flex-1 min-w-0 truncate">
        <span class="${subjectClass}">${escapeHtml(subject)}</span>${errorIcon}
      </span>
      <span class="${timeClass} text-xs shrink-0 w-16 text-right whitespace-nowrap">${escapeHtml(when)}</span>
    </li>`;
}

// Floating col 3 panel — header / body / footer. Reuses the same
// [data-approve] / [data-skip] hooks that _post() already handles.
function panelContent(a) {
  const subject = summaryText(a);
  const when = relativeTime(a.createdAt);
  const status = a.status || 'unknown';
  const draft = a.draft && typeof a.draft === 'object' ? a.draft : {};
  const toolCalls = Array.isArray(a.toolCalls) ? a.toolCalls : [];
  const callsHtml = toolCalls.map(c => `
    <li class="text-xs flex items-center gap-2">
      <span class="${c.status === 'success' ? 'text-success' : 'text-error'}">${c.status === 'success' ? '✓' : '✗'}</span>
      <code class="font-mono">${escapeHtml(c.name || 'unknown')}</code>
    </li>`).join('');
  const reasoning = (a.reasoning || '').trim();
  const showActions = status === 'draft';
  return `
    <header class="p-4 border-b border-base-300 flex items-start justify-between gap-3 shrink-0">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="${statusDot(status)} shrink-0" title="${escapeAttr(status)}"></span>
          <p class="font-medium text-sm truncate">${escapeHtml(a.agentName || 'Agent')}</p>
          <span class="text-[11px] text-base-content/40 ml-auto shrink-0">${escapeHtml(when)}</span>
        </div>
        <p class="text-base font-semibold mt-1.5 break-words">${escapeHtml(subject)}</p>
      </div>
      <button type="button" class="btn btn-ghost btn-square btn-sm" data-close-panel aria-label="Close">
        <i class="fa-regular fa-xmark"></i>
      </button>
    </header>
    <div class="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
      ${draft.summary && draft.summary !== subject ? `<p class="text-base-content/80 whitespace-pre-wrap">${escapeHtml(draft.summary)}</p>` : ''}
      ${draft.tool ? `<p class="text-xs text-base-content/60">Dispatcher: <code class="font-mono">${escapeHtml(draft.tool)}</code></p>` : ''}
      ${a.error ? `<div class="alert alert-error alert-sm text-xs"><i class="fa-regular fa-triangle-exclamation"></i><span>${escapeHtml(a.error)}</span></div>` : ''}
      ${callsHtml ? `
        <details data-reasoning-for="${escapeAttr(a._id)}-tools" class="text-xs">
          <summary class="cursor-pointer text-base-content/60 hover:text-base-content/80">Tool calls (${toolCalls.length})</summary>
          <ul class="space-y-1 mt-2">${callsHtml}</ul>
        </details>` : ''}
      ${reasoning ? `
        <details data-reasoning-for="${escapeAttr(a._id)}" class="text-xs">
          <summary class="cursor-pointer text-base-content/60 hover:text-base-content/80">Reasoning</summary>
          <pre class="whitespace-pre-wrap mt-2 p-2 bg-base-200 rounded text-[11px]">${escapeHtml(reasoning)}</pre>
        </details>` : ''}
    </div>
    ${showActions ? `
      <footer class="p-3 border-t border-base-300 flex justify-end gap-2 shrink-0">
        <button class="btn btn-ghost btn-sm" data-skip="${escapeAttr(a._id)}">Skip</button>
        <button class="btn btn-primary btn-sm" data-approve="${escapeAttr(a._id)}">Approve</button>
      </footer>` : ''}
  `;
}

// ── Helpers ──

function summaryText(a) {
  // Phase 1: nothing fills draft.summary, so derive a one-liner from what we have.
  if (a.draft && typeof a.draft === 'object') {
    if (a.draft.subject) return a.draft.subject;
    if (a.draft.summary) return a.draft.summary;
  }
  if (a.observation && a.observation.summary) return a.observation.summary;
  if (a.trigger?.kind === 'schedule') return `Scheduled: ${a.trigger.payload?.cron || 'cron'}`;
  if (a.trigger?.kind === 'change-stream') return `Reacted to ${a.trigger.payload?.collection || 'collection'} change`;
  return `${a.trigger?.kind || 'action'}`;
}

function statusDot(status) {
  const classes = {
    draft: 'inline-block w-2 h-2 rounded-full bg-warning',
    pending: 'inline-block w-2 h-2 rounded-full bg-info',
    approved: 'inline-block w-2 h-2 rounded-full bg-info',
    executed: 'inline-block w-2 h-2 rounded-full bg-success',
    skipped: 'inline-block w-2 h-2 rounded-full bg-base-content/30',
    failed: 'inline-block w-2 h-2 rounded-full bg-error',
    reversed: 'inline-block w-2 h-2 rounded-full bg-base-content/40'
  };
  return classes[status] || classes.pending;
}

function relativeTime(iso) {
  if (!iso) return '';
  const t = typeof iso === 'string' ? Date.parse(iso) : (iso instanceof Date ? iso.getTime() : iso);
  if (!Number.isFinite(t)) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  for (const { unit, max, divisor } of RELATIVE_TIME_THRESHOLDS) {
    if (diffSec < max) {
      const n = Math.max(1, Math.round(diffSec / divisor));
      return `${n}${unit[0]}`;
    }
  }
  const days = Math.round(diffSec / 86_400);
  return `${days}d`;
}

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function escapeAttr(s) {
  return escapeHtml(s);
}

// CSS attribute selector escape — used when looking up an element by an
// id that may contain special characters (`"` is the obvious one). Same
// shape as CSS.escape but available in older browsers too.
function cssAttrEscape(s) {
  return String(s).replace(/(["\\])/g, '\\$1');
}

if (!customElements.get('g-autopilot')) {
  customElements.define('g-autopilot', GAutopilot);
}

